<template>
  <div class="textarea-length-limitation">
    <span
      class="textarea-length-limitation__label"
      v-if="showTitle && title"
    >{{ title }}</span>
    <div class="textarea-length-limitation__only-print">
      <b>{{ placeholder }}: </b>
      {{ value || '-' }}
    </div>
    <textarea
      class="textarea-length-limitation__textarea"
      id="position-name"
      ref="textarea"
      v-bind:disabled="disabled"
      v-bind:aria-invalid="hasViolations"
      v-bind:aria-label="title"
      v-bind:maxlength="maxLength"
      v-bind:name="name"
      v-bind:placeholder="placeholder"
      v-bind:required="!optional"
      v-bind:rows="rows"
      v-bind:value="value"
      v-on:input="onInput"
      v-on:click="onUpdateCursorPosition"
      v-on:keyup="onUpdateCursorPosition"
      v-on:focus="onFocus($event); onUpdateCursorPosition($event)"
      v-on:blur="onBlur" />
    <div
      class="textarea-length-limitation__counter"
      v-if="showCounter"
    >{{ currentTextLength }}/{{ maxLength }}</div>
    <button
      v-if="isShowCleanerButton && !disabled"
      class="textarea-length-limitation__cross-cleaner"
      type="button"
      title="Очистить"
      v-on:click="onClean"
    />
    <div
      v-for="(error, index) in violations"
      v-bind:key="index"
      class="textarea-length-limitation__validate-error"
    >{{ error }}</div>
  </div>
</template>

<script>
/**
 * @description Нормализующее значение для высоты поля.
 * @type {number}
 */
const NORMALIZE_HEIGHT = 2;
/**
 * @description Компонент многострочного текстового поля.
 * @type {any}
 */
export default {
  mounted() {
    if (this.isAutoHeightExpanding) {
      const { textarea } = this.$refs;
      textarea.style.height = `${textarea.scrollHeight + NORMALIZE_HEIGHT}px`;
    }
  },
  computed: {
    /**
     * @description Имеет нарушения.
     * @type {boolean}
     */
    hasViolations() {
      return this.violations.length > 0;
    },
    /**
     * @description Длина текста.
     * @type {number}
     */
    currentTextLength() {
      return this.value.length || 0;
    },
  },
  methods: {
    /**
     * @description Обработать ввод.
     * @param {InputEvent} event
     */
    onInput(event) {
      this.$emit('input', event.target.value);
      this.textAvailability(event.target.value);
    },
    /**
     * @description Обработать фокус.
     * @param {InputEvent} event
     */
    onFocus(event) {
      this.$emit('focus', event);
    },
    /**
     * @description Обработать потерю фокуса.
     * @param {InputEvent} event
     */
    onBlur(event) {
      this.$emit('blur', event);
    },
    onUpdateCursorPosition(event) {
      const position = event.target.selectionStart;
      this.$emit('cursor-position', position);
    },
    /**
     * @description Инициализировать событие наличия текста
     * @param val
     */
    textAvailability(val) {
      const textLength = val.trim().length;
      this.$emit('text-availability', textLength > 0);
    },
    /**
     * @description Очистить поле
     * @returns {void}
     */
    onClean() {
      this.$emit('clean');
    },
  },
  name: 'TextareaLengthLimitation',
  props: {
    /**
     * @description Максимальная длина текста.
     * @type {number}
     */
    maxLength: { default: false, type: [Number, Boolean] },
    /**
     * @description Уникальное имя элемента формы.
     * @type {String}
     */
    name: { default: () => '', type: String },
    /**
     * @description Допускается необязательный ввод.
     * @type {boolean}
     */
    optional: {
      default: () => true,
      type: Boolean,
    },
    /**
     * @description Текст внутри поля.
     * @type {string}
     */
    placeholder: { type: String, default: '' },
    /**
     * @description Количество строк.
     * @type {string|number}
     */
    rows: { default: () => '1', type: [String, Number] },
    /**
     * @description Показывать счетчик текущей длины / максимальной длины текста.
     * @type {boolean}
     */
    showCounter: { type: Boolean, default: true },
    /**
     * @description Показать заголовок.
     * @type {boolean}
     */
    showTitle: {
      default: () => true,
      required: false,
      type: Boolean,
    },
    /**
     * @description Автоматическое растягивание высоты поля по содержимому.
     * @type {boolean}
     */
    isAutoHeightExpanding: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Показывать кнопку очистки
     * @type {boolean}
     */
    isShowCleanerButton: {
      default: () => false,
      required: false,
      type: Boolean,
    },
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: { type: String, default: '' },
    /**
     * @description Текст.
     * @type {string}
     */
    value: { default: '', type: String },
    /**
     * @description Нарушение ограничений.
     * @type {string}
     */
    violations: { default: () => [], type: Array },
    disabled: {
      default: () => false,
      type: Boolean,
    },
  },
};
</script>

<style scoped lang="scss">
.textarea-length-limitation {
  position: relative;
  &:focus {
    background-color: var(--primary-background-color);
    border: 2px solid var(--action-color);
    margin: 0;
    outline: 0;
  }
  & &__counter {
    display: flex;
    font-size: .7em;
    justify-content: flex-end;
  }
  & &__label {
    color: var(--unfocus-color);
    display: flex;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin: 0 0 2px 0;
  }
  & &__only-print {
    display: none;
  }
  & &__cross-cleaner {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 17px;
    height: 17px;
    background-image: url("@/assets/img/cross/cross-rounded-grey.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 11px 11px;
    cursor: pointer;
    transition: background-image 0.3s;
    &:hover {
      background-image: url("@/assets/img/cross/cross-rounded-blue.svg");
    }
  }
  & &__textarea {
    appearance: none;
    background: var(--primary-background-color);
    border-radius: 4px;
    border: 1px solid var(--border-color);
    color: var(--reading-color);
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 2px 0;
    outline: none;
    padding: 10px;
    padding-right: 35px;
    width: 100%;
    &:disabled {
      pointer-events: auto;
    }
  }
  & &__validate-error {
    color: var(--warning-color);
    font-size: .8rem;
  }
}
@media print {
  .textarea-length-limitation {
    & &__counter,
    & &__textarea {
      display: none;
    }
    & &__only-print {
      display: block !important;
    }
  }
}
</style>
